import { collection, doc, getDocs, setDoc } from "firebase/firestore";
import { db } from "./firebase";

function norm(s) {
  return String(s || "").trim();
}

function normLC(s) {
  return norm(s).toLowerCase();
}

function keyId(parts) {
  return parts
    .map(s => normLC(s).replace(/\s+/g, "_"))
    .filter(Boolean)
    .join("__");
}

function parseSchedule(schedule) {
  const raw = norm(schedule);
  if (!raw) return [];

  const m = raw.match(/^(.+?)\s+(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/i);
  if (!m) return [{ day: "", start: "", end: "", raw }];
  const daysPart = m[1];
  const start = m[2];
  const end = m[3];
  const days = daysPart
    .split(/[/,]/g)
    .map(d => norm(d))
    .filter(Boolean);
  if (days.length === 0) return [{ day: "", start, end, raw }];
  return days.map(day => ({ day, start, end, raw }));
}

export function classScheduleDocId({ campus, gradeLevel, section }) {
  return keyId([campus, gradeLevel, section]);
}

export function buildScheduleEntriesForSection({ campus, gradeLevel, section, teacherWorkloadsCurrentDocs }) {
  const campusLC = normLC(campus);
  const gradeNorm = normLC(gradeLevel);
  const sectionNorm = normLC(section);
  const out = [];

  teacherWorkloadsCurrentDocs
    .filter(w => normLC(w.campus) === campusLC)
    .forEach(w => {
      const teacher = norm(w.teacherName) || norm(w.displayName) || norm(w.email);
      const classes = Array.isArray(w.classes) ? w.classes : [];
      classes.forEach(c => {
        if (normLC(c.grade) !== gradeNorm) return;
        if (normLC(c.section) !== sectionNorm) return;
        const parsed = parseSchedule(c.schedule);
        if (parsed.length === 0) {
          out.push({
            day: "",
            start: "",
            end: "",
            subject: norm(c.subject),
            teacher,
            room: norm(c.room),
            gradebookUrl: norm(c.gradebookUrl)
          });
          return;
        }
        parsed.forEach(p => {
          out.push({
            day: p.day,
            start: p.start,
            end: p.end,
            subject: norm(c.subject),
            teacher,
            room: norm(c.room),
            gradebookUrl: norm(c.gradebookUrl)
          });
        });
      });
    });

  out.sort((a, b) => String(a.day || "").localeCompare(String(b.day || "")) || String(a.start || "").localeCompare(String(b.start || "")));
  return out;
}

export async function rebuildClassSchedulesCurrent({ campus, schoolYear, sections }) {
  const campusLC = normLC(campus);
  const snap = await getDocs(collection(db, "teacher_workloads_current"));
  const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  const unique = new Map();
  rows.forEach(r => {
    const k = String(r.sourceDocId || r.id || "");
    if (!k) return;
    const prev = unique.get(k);
    if (!prev) {
      unique.set(k, r);
      return;
    }
    const a = String(prev.updatedAt || "");
    const b = String(r.updatedAt || "");
    if (b > a) {
      unique.set(k, r);
      return;
    }
    if (b === a && String(r.id || "") < String(prev.id || "")) {
      unique.set(k, r);
    }
  });
  const docs = Array.from(unique.values());

  const targets = (Array.isArray(sections) ? sections : [])
    .map(s => ({ gradeLevel: norm(s.gradeLevel || s.grade), section: norm(s.section) }))
    .filter(s => s.gradeLevel && s.section);

  const uniqueTargets = new Map();
  targets.forEach(t => uniqueTargets.set(`${t.gradeLevel}__${t.section}`, t));

  const writes = Array.from(uniqueTargets.values()).map(async t => {
    const id = classScheduleDocId({ campus: campusLC, gradeLevel: t.gradeLevel, section: t.section });
    const rawEntries = buildScheduleEntriesForSection({
      campus: campusLC,
      gradeLevel: t.gradeLevel,
      section: t.section,
      teacherWorkloadsCurrentDocs: docs
    });
    const entriesByKey = new Map();
    rawEntries.forEach(e => {
      const k = [
        normLC(e.day),
        normLC(e.start),
        normLC(e.end),
        normLC(e.subject),
        normLC(e.teacher),
        normLC(e.room)
      ].join("|");
      if (!k) return;
      const prev = entriesByKey.get(k);
      if (!prev) {
        entriesByKey.set(k, e);
        return;
      }
      const prevUrl = norm(prev.gradebookUrl);
      const nextUrl = norm(e.gradebookUrl);
      if (!prevUrl && nextUrl) entriesByKey.set(k, e);
    });
    const entries = Array.from(entriesByKey.values());
    const ref = doc(db, "class_schedules_current", id);
    await setDoc(ref, {
      campus: campusLC,
      schoolYear: norm(schoolYear),
      gradeLevel: norm(t.gradeLevel),
      section: norm(t.section),
      entries,
      updatedAt: new Date().toISOString()
    }, { merge: true });
  });

  await Promise.all(writes);
  return true;
}
